// src/Splash.js
import React, { useEffect, useRef, useState } from "react";
import BorkenburgLion from "./BorkenburgLion";
import "./Splash.css";

/* Must match the animation lengths in Splash.css. */
const ROAR_MS = 1750;
const STATIC_MS = 900; /* reduced-motion: no roar to wait out */
const FADE_MS = 300;

const SEEN_KEY = "bb:splash-seen";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Whether the load screen should play. It is a once-per-session flourish: a
 * roaring lion is a welcome on arrival and an obstacle on every refresh after.
 * Storage access throws in some private-browsing modes, so failure means
 * "just play it" rather than breaking the page.
 */
export function shouldShowSplash() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SEEN_KEY) !== "1";
  } catch {
    return true;
  }
}

function markSeen() {
  try {
    window.sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* private mode — the splash simply replays next load */
  }
}

/**
 * Roaring-lion load screen. Renders as a fixed overlay on top of the already
 * mounted app rather than in place of it, so content, routing and analytics are
 * never held up waiting for the animation.
 *
 * @param {{onFinish: () => void}} props
 */
export default function Splash({ onFinish }) {
  const [leaving, setLeaving] = useState(false);

  // Keep the callback in a ref so an inline arrow from the parent cannot
  // restart the timers on re-render.
  const finish = useRef(onFinish);
  useEffect(() => {
    finish.current = onFinish;
  }, [onFinish]);

  useEffect(() => {
    markSeen();
    const hold = prefersReducedMotion() ? STATIC_MS : ROAR_MS;

    const fadeTimer = setTimeout(() => setLeaving(true), hold);
    const doneTimer = setTimeout(() => finish.current?.(), hold + FADE_MS);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, []);

  return (
    <div
      className={`splash-screen is-roaring${leaving ? " is-leaving" : ""}`}
      /* Decorative: screen readers should go straight to the page behind it. */
      aria-hidden="true"
    >
      <div className="splash-inner">
        <div className="splash-lion">
          <BorkenburgLion rings />
        </div>
        <div className="progress-bar">
          <div className="progress-fill" />
        </div>
      </div>
    </div>
  );
}
